/**
 * Custom hook for PostHog event tracking
 *
 * Provides type-safe event tracking with automatic no-op when PostHog is disabled
 */

import { useCallback } from 'react'
import { posthog, isPostHogEnabled } from '@/lib/posthog'

// Define key events for type safety
export type PostHogEvent =
  // User authentication
  | 'user_signed_up'
  | 'user_signed_in'
  | 'user_signed_out'

  // Flashcard events
  | 'flashcard_reviewed'
  | 'flashcard_created'
  | 'flashcard_session_completed'

  // Song generation events
  | 'song_generation_started'
  | 'song_generation_completed'
  | 'song_generation_failed'
  | 'song_played'

  // Settings & accumulation control
  | 'settings_viewed'
  | 'settings_changed'
  | 'settings_saved'
  | 'settings_reset_to_defaults'
  | 'faq_viewed'
  | 'faq_item_expanded'

  // Other interactions
  | 'button_clicked'
  | 'feature_used'

export interface PostHogEventProperties {
  // Flashcard properties
  grade?: number
  cardType?: string
  sessionCardsReviewed?: number
  sessionDuration?: number

  // Song properties
  songType?: 'song' | 'instrumental' | 'lyrics' | 'stems'
  model?: string
  includeWav?: boolean
  generationId?: string

  // Settings properties
  settingName?: string
  oldValue?: number | boolean | string
  newValue?: number | boolean | string
  maxDailyFlashcards?: number
  vacationModeThreshold?: number
  resetImmatureCards?: boolean
  resetMatureCards?: boolean

  // FAQ properties
  faqQuestion?: string
  faqCategory?: string

  // Generic properties
  [key: string]: string | number | boolean | undefined
}

export function usePostHog() {
  const trackEvent = useCallback((event: PostHogEvent, properties?: PostHogEventProperties) => {
    if (!isPostHogEnabled()) {
      return
    }

    try {
      posthog.capture(event, properties)
      console.log('[PostHog] Event tracked:', event, properties)
    } catch (error) {
      console.error('[PostHog] Event tracking error:', error)
    }
  }, [])

  return {
    trackEvent,
    isEnabled: isPostHogEnabled(),
  }
}
